import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useCurrency } from '@/hooks/useCurrency';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useAuth } from '@/hooks/useAuth';
import { usePersistentTabState } from '@/hooks/usePersistentTabState';
import { formatPriceWithCurrency } from '@/utils/currency';
import { toast } from 'sonner';
import { DollarSign, RefreshCw, Wallet, ArrowDownCircle, ArrowUpCircle, Download } from 'lucide-react';

interface CommissionWalletRow {
  id: string;
  currency: string;
  available_amount_cents: number;
  total_credited_cents: number;
  total_withdrawn_cents: number;
  created_at: string;
  updated_at: string;
}

interface CommissionTransactionRow {
  id: string;
  wallet_id: string;
  type: 'commission_credit' | 'withdrawal' | 'adjustment';
  amount_cents: number;
  currency: string;
  source_order_id: string | null;
  vendor_id: string | null;
  description: string | null;
  metadata: Record<string, unknown> | null;
  created_by: string | null;
  created_at: string;
}

interface CommissionWithdrawalRow {
  id: string;
  wallet_id: string;
  amount_cents: number;
  currency: string;
  status?: 'pending' | 'completed' | 'rejected' | 'failed';
  payment_method?: 'internal' | 'mobile_money' | 'bank_transfer';
  destination: string | null;
  notes: string | null;
  rejected_reason?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  transaction_reference?: string | null;
  created_by: string | null;
  created_at: string;
}

interface WithdrawalRpcResult {
  success?: boolean;
  error?: string;
  available?: number;
  transaction_id?: string;
  status?: 'pending' | 'completed' | 'rejected' | 'failed';
  message?: string;
  auto_validated?: boolean;
  withdrawal_id?: string;
}

const parseAmountToCents = (rawValue: string): number => {
  const normalized = rawValue.replace(',', '.').trim();
  const value = Number.parseFloat(normalized);

  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }

  return Math.round(value * 100);
};

const getTxBadge = (type: CommissionTransactionRow['type']) => {
  if (type === 'commission_credit') {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Crédit commission</Badge>;
  }

  if (type === 'withdrawal') {
    return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Retrait admin</Badge>;
  }

  return <Badge variant="outline">Ajustement</Badge>;
};

const getWithdrawalStatusBadge = (status?: CommissionWithdrawalRow['status']) => {
  switch (status) {
    case 'pending':
      return <Badge variant="secondary">En attente validation</Badge>;
    case 'completed':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Complété</Badge>;
    case 'rejected':
      return <Badge variant="destructive">Rejeté</Badge>;
    case 'failed':
      return <Badge variant="destructive">Échec</Badge>;
    default:
      return <Badge variant="outline">-</Badge>;
  }
};

export const AdminCommissionWalletManager = () => {
  const { displayCurrency, convertPrice } = useCurrency();
  const { settings } = useSiteSettings();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [wallets, setWallets] = useState<CommissionWalletRow[]>([]);
  const [transactions, setTransactions] = useState<CommissionTransactionRow[]>([]);
  const [withdrawals, setWithdrawals] = useState<CommissionWithdrawalRow[]>([]);

  const [selectedCurrency, setSelectedCurrency] = useState('CDF');
  const [txCurrencyFilter, setTxCurrencyFilter] = useState('all');
  const [wdCurrencyFilter, setWdCurrencyFilter] = useState('all');
  const [historyTab, setHistoryTab] = usePersistentTabState(
    'admin-commission-wallet:historyTab',
    'transactions',
    ['transactions', 'withdrawals']
  );
  const [confirmWithdrawOpen, setConfirmWithdrawOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);

      const sb = supabase as any;
      const [walletRes, txRes, wdRes] = await Promise.all([
        sb
          .from('admin_commission_wallet')
          .select('*')
          .order('currency', { ascending: true }),
        sb
          .from('admin_commission_transactions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100),
        sb
          .from('admin_commission_withdrawals')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50),
      ]);

      if (walletRes.error) throw walletRes.error;
      if (txRes.error) throw txRes.error;
      if (wdRes.error) throw wdRes.error;

      const walletRows = (walletRes.data || []) as CommissionWalletRow[];

      setWallets(walletRows);
      setTransactions((txRes.data || []) as CommissionTransactionRow[]);
      setWithdrawals((wdRes.data || []) as CommissionWithdrawalRow[]);

      if (walletRows.length > 0) {
        const preferredWallet =
          walletRows.find((wallet) => wallet.currency === selectedCurrency) ||
          walletRows.find((wallet) => wallet.available_amount_cents > 0) ||
          walletRows[0];
        setSelectedCurrency(preferredWallet.currency);
      }
    } catch (error) {
      console.error('Error fetching admin commission wallet data:', error);
      toast.error('Erreur lors du chargement du wallet commission');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const selectedWallet = useMemo(
    () => wallets.find((wallet) => wallet.currency === selectedCurrency) ?? null,
    [wallets, selectedCurrency]
  );

  const stats = useMemo(() => {
    return wallets.reduce(
      (acc, wallet) => {
        acc.available += convertPrice(wallet.available_amount_cents, wallet.currency, displayCurrency);
        acc.credited += convertPrice(wallet.total_credited_cents, wallet.currency, displayCurrency);
        acc.withdrawn += convertPrice(wallet.total_withdrawn_cents, wallet.currency, displayCurrency);
        return acc;
      },
      { available: 0, credited: 0, withdrawn: 0 }
    );
  }, [wallets, convertPrice, displayCurrency]);

  const defaultDestination = useMemo(() => {
    const configuredDestination = settings?.commission_wallet_default_destination;
    if (typeof configuredDestination === 'string' && configuredDestination.trim().length > 0) {
      return configuredDestination.trim();
    }
    return 'Compte principal Nazandoo';
  }, [settings]);

  const transactionCurrencyOptions = useMemo(
    () => Array.from(new Set(transactions.map((tx) => tx.currency))).sort(),
    [transactions]
  );

  const withdrawalCurrencyOptions = useMemo(
    () => Array.from(new Set(withdrawals.map((wd) => wd.currency))).sort(),
    [withdrawals]
  );

  const filteredTransactions = useMemo(() => {
    if (txCurrencyFilter === 'all') return transactions;
    return transactions.filter((tx) => tx.currency === txCurrencyFilter);
  }, [transactions, txCurrencyFilter]);

  const filteredWithdrawals = useMemo(() => {
    if (wdCurrencyFilter === 'all') return withdrawals;
    return withdrawals.filter((wd) => wd.currency === wdCurrencyFilter);
  }, [withdrawals, wdCurrencyFilter]);

  const downloadCsv = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const escapeCsv = (value: string | number | null | undefined) => {
    const raw = value == null ? '' : String(value);
    const escaped = raw.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const exportTransactionsCsv = () => {
    const rows = filteredTransactions.map((tx) => [
      tx.id,
      tx.created_at,
      tx.type,
      tx.amount_cents,
      tx.currency,
      tx.description || '',
      tx.source_order_id || '',
      tx.vendor_id || '',
    ]);

    const header = ['id', 'created_at', 'type', 'amount_cents', 'currency', 'description', 'source_order_id', 'vendor_id'];
    const csv = [header, ...rows]
      .map((line) => line.map((col) => escapeCsv(col)).join(','))
      .join('\n');

    downloadCsv(`commission_transactions_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportWithdrawalsCsv = () => {
    const rows = filteredWithdrawals.map((wd) => [
      wd.id,
      wd.created_at,
      wd.amount_cents,
      wd.currency,
      wd.destination || '',
      wd.notes || '',
      wd.created_by || '',
    ]);

    const header = ['id', 'created_at', 'amount_cents', 'currency', 'destination', 'notes', 'created_by'];
    const csv = [header, ...rows]
      .map((line) => line.map((col) => escapeCsv(col)).join(','))
      .join('\n');

    downloadCsv(`commission_withdrawals_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handleWithdraw = async () => {
    const amountCents = parseAmountToCents(amount);

    if (!selectedWallet) {
      toast.error('Aucun wallet disponible pour cette devise');
      return;
    }

    if (amountCents <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    if (amountCents > selectedWallet.available_amount_cents) {
      toast.error('Montant supérieur au solde disponible');
      return;
    }

    setConfirmWithdrawOpen(true);
  };

  const confirmWithdraw = async () => {
    const amountCents = parseAmountToCents(amount);

    if (!selectedWallet || amountCents <= 0 || amountCents > selectedWallet.available_amount_cents) {
      setConfirmWithdrawOpen(false);
      toast.error('Vérifiez le montant avant de confirmer');
      return;
    }

    try {
      setProcessing(true);

      const sb = supabase as any;
      const { data, error } = await sb.rpc('request_admin_commission_withdrawal', {
        p_amount_cents: amountCents,
        p_currency: selectedCurrency,
        p_destination: defaultDestination,
        p_notes: notes.trim() || null,
        p_payment_method: 'internal',
      });

      if (error) throw error;

      const result = (data || {}) as WithdrawalRpcResult;
      if (!result.success) {
        const baseError = result.error || 'Erreur lors du retrait';
        if (baseError === 'insufficient_funds') {
          toast.error('Fonds insuffisants pour ce retrait');
          return;
        }
        toast.error(baseError);
        return;
      }

      if (result.status === 'pending') {
        toast.success('Demande envoyée. En attente de validation super-admin');
        if (result.withdrawal_id) {
          try {
            await supabase.functions.invoke('send-admin-commission-withdrawal-notification', {
              body: {
                action: 'request_submitted',
                withdrawalId: result.withdrawal_id,
              },
            });
          } catch (notifyError) {
            console.warn('Failed to send pending approval email notification', notifyError);
          }
        }
      } else {
        toast.success(result.auto_validated ? 'Retrait auto-validé (super-admin) avec succès' : 'Retrait effectué avec succès');
      }
      setConfirmWithdrawOpen(false);
      setAmount('');
      setNotes('');
      await fetchData();
    } catch (error: any) {
      console.error('Error withdrawing from commission wallet:', error);
      toast.error(error?.message || 'Erreur lors du retrait');
    } finally {
      setProcessing(false);
    }
  };

  const handleApprovePendingWithdrawal = async (withdrawalId: string) => {
    try {
      setProcessing(true);
      const sb = supabase as any;
      const { data, error } = await sb.rpc('approve_admin_commission_withdrawal', {
        p_withdrawal_id: withdrawalId,
        p_notes: null,
        p_transaction_reference: null,
      });

      if (error) throw error;
      const result = (data || {}) as WithdrawalRpcResult;
      if (!result.success) throw new Error(result.error || 'Erreur lors de la validation');

      try {
        await supabase.functions.invoke('send-admin-commission-withdrawal-notification', {
          body: {
            action: 'request_approved',
            withdrawalId,
          },
        });
      } catch (notifyError) {
        console.warn('Failed to send approval email notification', notifyError);
      }

      toast.success('Demande validée et retrait exécuté');
      await fetchData();
    } catch (error: any) {
      toast.error(error?.message || 'Erreur lors de la validation');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectPendingWithdrawal = async (withdrawalId: string) => {
    try {
      setProcessing(true);
      const sb = supabase as any;
      const { data, error } = await sb.rpc('reject_admin_commission_withdrawal', {
        p_withdrawal_id: withdrawalId,
        p_reason: 'Rejected by super-admin',
        p_notes: null,
      });

      if (error) throw error;
      const result = (data || {}) as WithdrawalRpcResult;
      if (!result.success) throw new Error(result.error || 'Erreur lors du rejet');

      try {
        await supabase.functions.invoke('send-admin-commission-withdrawal-notification', {
          body: {
            action: 'request_rejected',
            withdrawalId,
          },
        });
      } catch (notifyError) {
        console.warn('Failed to send rejection email notification', notifyError);
      }

      toast.success('Demande rejetée');
      await fetchData();
    } catch (error: any) {
      toast.error(error?.message || 'Erreur lors du rejet');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disponible total</CardTitle>
            <Wallet className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPriceWithCurrency(Math.round(stats.available), displayCurrency, displayCurrency, convertPrice)}
            </div>
            <p className="text-xs text-muted-foreground">Toutes devises confondues (converties)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total crédité</CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPriceWithCurrency(Math.round(stats.credited), displayCurrency, displayCurrency, convertPrice)}
            </div>
            <p className="text-xs text-muted-foreground">Commissions collectées</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total retiré</CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPriceWithCurrency(Math.round(stats.withdrawn), displayCurrency, displayCurrency, convertPrice)}
            </div>
            <p className="text-xs text-muted-foreground">Retraits admin validés</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Wallet commissions plateforme
              </CardTitle>
              <CardDescription>
                Consulter les soldes par devise et effectuer des retraits admin
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={processing}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            {wallets.length === 0 ? (
              <div className="md:col-span-3 rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                Aucun wallet commission disponible pour le moment
              </div>
            ) : (
              wallets.map((wallet) => (
                <button
                  key={wallet.id}
                  type="button"
                  onClick={() => setSelectedCurrency(wallet.currency)}
                  className={`rounded-lg border p-4 space-y-2 text-left transition-colors ${
                    selectedCurrency === wallet.currency
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{wallet.currency}</p>
                    <Badge variant={selectedCurrency === wallet.currency ? 'default' : 'outline'}>
                      {selectedCurrency === wallet.currency ? 'Actif' : wallet.currency}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Disponible</p>
                  <p className="text-xl font-bold">
                    {formatPriceWithCurrency(wallet.available_amount_cents, wallet.currency, wallet.currency, convertPrice)}
                  </p>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>
                      Crédité: {formatPriceWithCurrency(wallet.total_credited_cents, wallet.currency, wallet.currency, convertPrice)}
                    </p>
                    <p>
                      Retiré: {formatPriceWithCurrency(wallet.total_withdrawn_cents, wallet.currency, wallet.currency, convertPrice)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <h4 className="font-semibold">Nouveau retrait admin</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Devise</Label>
                <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une devise" />
                  </SelectTrigger>
                  <SelectContent>
                    {wallets.map((wallet) => (
                      <SelectItem key={wallet.currency} value={wallet.currency}>
                        {wallet.currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Vous pouvez choisir la devise ici ou cliquer une carte wallet.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Montant</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Disponible: {selectedWallet
                    ? formatPriceWithCurrency(
                        selectedWallet.available_amount_cents,
                        selectedWallet.currency,
                        selectedWallet.currency,
                        convertPrice
                      )
                    : '-'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Destination par défaut Nazandoo</Label>
              <Input value={defaultDestination} disabled />
            </div>

            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                placeholder="Motif, référence interne, etc."
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>

            <Button onClick={handleWithdraw} disabled={processing || wallets.length === 0}>
              {processing ? 'Traitement...' : 'Retirer depuis le wallet'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={historyTab} onValueChange={setHistoryTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="withdrawals">Historique des retraits</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Transactions de commissions</CardTitle>
                  <CardDescription>Crédits de commissions et retraits admin</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={txCurrencyFilter} onValueChange={setTxCurrencyFilter}>
                    <SelectTrigger className="w-[170px]">
                      <SelectValue placeholder="Filtrer devise" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes devises</SelectItem>
                      {transactionCurrencyOptions.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={exportTransactionsCsv}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredTransactions.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucune transaction trouvée</p>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Devise</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTransactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="font-medium">
                            {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell>{getTxBadge(tx.type)}</TableCell>
                          <TableCell className="font-semibold">
                            {formatPriceWithCurrency(tx.amount_cents, tx.currency, tx.currency, convertPrice)}
                          </TableCell>
                          <TableCell>{tx.currency}</TableCell>
                          <TableCell className="max-w-xs truncate">{tx.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="withdrawals" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Retraits admin</CardTitle>
                  <CardDescription>Journal des retraits du wallet commission</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={wdCurrencyFilter} onValueChange={setWdCurrencyFilter}>
                    <SelectTrigger className="w-[170px]">
                      <SelectValue placeholder="Filtrer devise" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes devises</SelectItem>
                      {withdrawalCurrencyOptions.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={exportWithdrawalsCsv}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredWithdrawals.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucun retrait trouvé</p>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Devise</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredWithdrawals.map((wd) => (
                        <TableRow key={wd.id}>
                          <TableCell className="font-medium">
                            {new Date(wd.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatPriceWithCurrency(wd.amount_cents, wd.currency, wd.currency, convertPrice)}
                          </TableCell>
                          <TableCell>{wd.currency}</TableCell>
                          <TableCell>{getWithdrawalStatusBadge(wd.status)}</TableCell>
                          <TableCell>{wd.destination || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{wd.notes || '-'}</TableCell>
                          <TableCell>
                            {isSuperAdmin && wd.status === 'pending' ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApprovePendingWithdrawal(wd.id)}
                                  disabled={processing}
                                >
                                  Valider
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectPendingWithdrawal(wd.id)}
                                  disabled={processing}
                                >
                                  Rejeter
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmWithdrawOpen} onOpenChange={setConfirmWithdrawOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le retrait admin</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez retirer {selectedWallet
                ? formatPriceWithCurrency(parseAmountToCents(amount), selectedWallet.currency, selectedWallet.currency, convertPrice)
                : '-'} depuis le wallet commission ({selectedCurrency}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Destination: {defaultDestination}</p>
            <p>Notes: {notes.trim() || 'Aucune'}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Annuler</AlertDialogCancel>
            <AlertDialogAction disabled={processing} onClick={confirmWithdraw}>
              {processing ? 'Traitement...' : 'Confirmer le retrait'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
